
import {
  FileDescriptor, FileDocument, Time, Label, User, fileDocumentLicenseOptions,
  FileDocumentStatus, DocumentFilter,
} from '@/model';
import api from '@/rest-api';
import { RouteNames } from '@/router/route-names';
import Vue from 'vue';
import DocumentEditor from '@/components/DocumentEditor.vue';
import IconButton from '@/components/IconButton.vue';
import LabelPanel from '@/components/LabelPanel.vue';
import labels, { labelSorter } from '@/store/labels';
import users from '@/store/users';
import { notEmpty, objectToArray } from '@/util/array-util';
import DocumentFilterEditor from '@/components/DocumentFilterEditor.vue';
import { documentPublishedLabel, formatDocumentPublished, titleForRoute } from '@/store/ui-config';

export default Vue.extend({
  name: 'DocumentsView',
  components: {
    DocumentEditor,
    LabelPanel,
    IconButton,
    DocumentFilterEditor,
  },

  props: {
    loading: { type: Boolean, default: false },
    documents: { type: Array as () => FileDocument[], default: () => [] },
    filter: { type: Object as () => DocumentFilter },
  },

  computed: {
    pageTitle(): string {
      return titleForRoute(this.$route.path) ?? 'Documents';
    },
    headers() {
      return [
        {
          text: 'Id', align: 'start', value: 'document.id',
        },
        { text: 'Source', value: 'document.publisher.name' },
        { text: 'Name', value: 'document.name' },
        { text: 'License', value: 'document.license' },
        { text: 'Status', value: 'document.status' },
        { text: documentPublishedLabel(), value: 'document.published' },
        { text: 'User', value: 'user.displayName' },
        { text: 'Created', value: 'document.file.persisted' },
        { text: 'Document Labels', value: 'document.labels', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false },
      ];
    },
    documentsWithUser(): {document: FileDocument; user: User | undefined } [] {
      return this.documents
        .map((document) => ({ document, user: users.getters.lookup.get(document.userId) }));
    },
    licenseOptions(): typeof fileDocumentLicenseOptions | undefined {
      return fileDocumentLicenseOptions;
    },
    isFilterEmpty(): boolean {
      return objectToArray(this.editFilter).every((v) => v === undefined);
    },
  },

  data() {
    return {
      edit: false,
      valid: false,
      showDetails: false,
      editItem: {} as Partial<FileDocument> | undefined,
      fileDetails: undefined as FileDescriptor | undefined,
      search: '',
      editFilter: {} as DocumentFilter,
      summaryStatues: [
        { value: true, text: 'Completed' },
        { value: false, text: 'Not started' },
      ],
    };
  },

  watch: {
    filter: {
      handler(v: DocumentFilter) {
        this.editFilter = { ...v };
      },
      immediate: true,
    },
  },

  methods: {
    input() {
      this.$emit('filterChange', this.editFilter);
    },
    saved(_: FileDocument) {
      this.$emit('load');
    },
    dateTime(t: Time) {
      return t && Time.asString(t);
    },
    date(t: Time) {
      return t && Time.asDateString(t);
    },
    published(t: Time) {
      return formatDocumentPublished(t);
    },
    isPdf(file?: FileDescriptor): boolean {
      return file?.mime === 'application/pdf';
    },
    showDocumentResult(doc: FileDocument) {
      this.$router.push({ name: RouteNames.DocumentResult, params: { documentId: `${doc.id}` } });
    },
    reingest(doc: FileDocument) {
      this.$emit('reingest', doc);
    },
    downloadUrl(file: FileDescriptor | undefined, forceSave?: boolean) {
      return file ? api.fileDownloadUri(file.uuid, forceSave) : undefined;
    },
    newDocument() {
      this.editItem = undefined;
      this.edit = true;
    },
    editDocument(item: FileDocument) {
      this.editItem = { ...item };
      this.edit = true;
    },

    labelLookup(labelIds: readonly number[] | undefined): Label[] {
      return this.lookups(labelIds, labels.getters.lookup)
        .sort(labelSorter(['type', 'alpha']));
    },
    lookups<T>(ids: readonly number[] | undefined, index: ReadonlyMap<number, T>): T[] {
      return ids?.map((id) => index.get(id)).filter(notEmpty) || [];
    },
    changeFilter(value: DocumentFilter) {
      this.editFilter = { ...value };
      this.input();
    },
    displayLicense(document: FileDocument): string | undefined {
      return document.license === undefined ? document.publisher?.license : document.license;
    },
    statusName(status: FileDocumentStatus) {
      return FileDocumentStatus.name(status);
    },
    resetFilter() {
      this.$emit('resetFilter');
    },
  },
});
